import { AdBreakDialog } from '@gagovictor/shared-frontend-core/dist/ads';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useT } from '../i18n';

import { useAdFree } from './adFree';
import { initializeAdMob, showRewarded } from './admob';
import { config, isNative, webBreakAdConfigured } from './config';

interface AdsContextValue {
    /**
     * The video gate in front of every reading. Resolves true when the reading may be shown: the video
     * was watched, or no ad could be shown. Resolves false when the person closed the video early.
     */
    requestUnlock: () => Promise<boolean>;
    /** False once the lifetime unlock is owned: no video, banner or ad break is shown. */
    adsEnabled: boolean;
}

const AdsContext = createContext<AdsContextValue>({
    requestUnlock: async () => true,
    adsEnabled: true,
});

export const useAds = () => useContext(AdsContext);

export const AdsProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const t = useT();
    const adsEnabled = !useAdFree();
    const [breakOpen, setBreakOpen] = useState(false);
    const pending = useRef<(() => void) | null>(null);
    const busy = useRef(false);

    useEffect(() => {
        if (adsEnabled) void initializeAdMob();
    }, [adsEnabled]);

    const closeBreak = useCallback(() => {
        setBreakOpen(false);
        pending.current?.();
        pending.current = null;
    }, []);

    const requestUnlock = useCallback(async () => {
        // The lifetime unlock: every reading opens at once.
        if (!adsEnabled) return true;
        if (busy.current) return false;
        busy.current = true;
        try {
            if (isNative()) {
                const result = await showRewarded();
                return result !== 'dismissed';
            }
            // The web build: a full-screen ad break that cannot be closed for a few seconds.
            if (!webBreakAdConfigured()) return true;
            await new Promise<void>((resolve) => {
                pending.current = resolve;
                setBreakOpen(true);
            });
            return true;
        } finally {
            busy.current = false;
        }
    }, [adsEnabled]);

    const value = useMemo(
        () => ({ requestUnlock, adsEnabled }),
        [requestUnlock, adsEnabled]
    );

    return (
        <AdsContext.Provider value={value}>
            {children}
            <AdBreakDialog
                open={breakOpen}
                onClose={closeBreak}
                client={config.adsenseClient}
                slot={config.adsenseBreakSlot}
                preview={config.adsPreview}
                test={config.adsenseTest}
                labels={{
                    title: t('ads.breakTitle'),
                    preview: t('ads.breakPreview'),
                    continue: t('ads.continue'),
                    continueIn: (seconds) => t('ads.continueIn', { seconds }),
                }}
            />
        </AdsContext.Provider>
    );
};
